/**
 * BaseChart
 *
 * Thin wrapper around `echarts-for-react` that automatically:
 * - Uses the BlueCopa ECharts theme from ChartProvider
 * - Handles responsive resizing
 * - Provides loading & empty states
 * - Applies consistent animation defaults
 *
 * All pre-built chart components (BarChart, LineChart, etc.) build on this.
 */

"use client";

import React, { useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption, ECharts } from "echarts";
import { useChartContext } from "./chart-provider";
import { chartAnimation } from "./chart-utils";
import { cn } from "@/src/utils/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BaseChartProps {
  /** Full ECharts option object */
  option: EChartsOption | Record<string, any>;
  /** Chart height — CSS value or number (px). Default "100%" */
  height?: string | number;
  /** Chart width — CSS value or number (px). Default "100%" */
  width?: string | number;
  /** Extra class names for the wrapper */
  className?: string;
  /** Style overrides for the wrapper */
  style?: React.CSSProperties;
  /** If true, show a loading spinner overlay */
  loading?: boolean;
  /** If true, show an empty state message */
  empty?: boolean;
  /** Custom empty state message */
  emptyText?: string;
  /** ECharts event handlers — e.g. { click: handler, mouseover: handler } */
  onEvents?: Record<string, (params: any) => void>;
  /** Group id for linked charts (e.g., tooltip sync across charts) */
  group?: string;
  /** Custom animation overrides */
  animationOptions?: Parameters<typeof chartAnimation>[0];
  /** Disable animation entirely */
  noAnimation?: boolean;
  /** Renderer: "canvas" (default) or "svg" */
  renderer?: "canvas" | "svg";
  /** Called when the chart instance is ready */
  onChartReady?: (instance: ECharts) => void;
  /** Accessible label describing the chart for screen readers */
  ariaLabel?: string;
  /** Detailed data summary for screen readers (hidden visually) */
  dataSummary?: string;
}

export interface BaseChartRef {
  /** Get the underlying ECharts instance */
  getInstance: () => ECharts | undefined;
  /** Force a resize */
  resize: () => void;
}

// ---------------------------------------------------------------------------
// Loading Spinner
// ---------------------------------------------------------------------------

function ChartLoading() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm z-10 rounded-theme-lg">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 rounded-full border-2 border-muted border-t-primary animate-spin" />
        <span className="text-xs text-muted-foreground">Loading chart…</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State
// ---------------------------------------------------------------------------

function ChartEmpty({ text = "No data available" }: { text?: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center z-10">
      <div className="flex flex-col items-center gap-2 text-muted-foreground">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-40">
          <path d="M3 3v18h18" />
          <path d="M7 16l4-8 4 4 4-6" />
        </svg>
        <span className="text-sm">{text}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BaseChart Component
// ---------------------------------------------------------------------------

export const BaseChart = forwardRef<BaseChartRef, BaseChartProps>(
  function BaseChart(
    {
      option,
      height = "100%",
      width = "100%",
      className,
      style,
      loading = false,
      empty = false,
      emptyText,
      onEvents,
      group,
      animationOptions,
      noAnimation = false,
      renderer = "canvas",
      onChartReady,
      ariaLabel,
      dataSummary,
    },
    ref
  ) {
    const chartRef = useRef<ReactECharts>(null);
    const { themeName, version } = useChartContext();

    // Merge animation defaults into the option
    const mergedOption = useMemo(() => {
      const anim = noAnimation
        ? { animation: false }
        : chartAnimation(animationOptions);
      return { ...anim, ...option };
    }, [option, animationOptions, noAnimation]);

    // Expose instance via ref
    useImperativeHandle(ref, () => ({
      getInstance: () => chartRef.current?.getEchartsInstance(),
      resize: () => chartRef.current?.getEchartsInstance()?.resize(),
    }));

    // Group linking
    useEffect(() => {
      if (group) {
        const instance = chartRef.current?.getEchartsInstance();
        if (instance) {
          instance.group = group;
        }
      }
    }, [group]);

    // Notify parent when chart is ready
    useEffect(() => {
      if (onChartReady) {
        const instance = chartRef.current?.getEchartsInstance();
        if (instance) onChartReady(instance);
      }
    }, [onChartReady]);

    // Force re-render when theme tokens change
    const key = `chart-${version}`;

    return (
      <div
        className={cn("relative", className)}
        style={{
          height: typeof height === "number" ? `${height}px` : height,
          width: typeof width === "number" ? `${width}px` : width,
          minHeight: 200,
          ...style,
        }}
        role="img"
        aria-label={ariaLabel}
      >
        {dataSummary && (
          <div className="sr-only" role="document" aria-label="Chart data">
            {dataSummary}
          </div>
        )}
        {loading && <ChartLoading />}
        {empty && <ChartEmpty text={emptyText} />}

        <ReactECharts
          key={key}
          ref={chartRef}
          option={mergedOption}
          theme={themeName}
          style={{ height: "100%", width: "100%" }}
          opts={{ renderer }}
          onEvents={onEvents}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    );
  }
);

export default BaseChart;
